/* 3SQM — shared video manager (the CLAUDE.md critical piece).
   ONE IntersectionObserver for every managed <video> on the page and a hard
   play budget (~3 mobile / ~6 desktop): a naïve wall of autoplaying loops
   breaks mobile decoders. Videos are `preload="none"` + poster; they only
   load & play while on screen, and pause as soon as they leave.
   Reduced motion: posters only — videos never start. */
(() => {
  if (window.__sqmVideo) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const BUDGET = window.matchMedia('(min-width: 750px)').matches ? 6 : 3;
  let playingCount = 0;

  // Release a video's reserved slot exactly once (idempotent via a per-video
  // flag), so pause + a failed play() can't double-decrement and the count
  // can't drift negative.
  const release = (video) => {
    if (!video.dataset.sqmReserved) return;
    delete video.dataset.sqmReserved;
    playingCount = Math.max(0, playingCount - 1);
  };

  const tryPlay = (video) => {
    if (reduceMotion.matches) return;
    if (video.dataset.sqmReserved) return; // already holds a slot
    if (playingCount >= BUDGET) return;
    if (!video.paused) return;
    // RESERVE the slot SYNCHRONOUSLY, before play(): the old code only
    // incremented on the async 'play' event, which fires AFTER this budget
    // check — so a burst of intersecting videos all passed the check and the
    // budget was momentarily blown. Reserve now; roll back if play() rejects.
    video.dataset.sqmReserved = 'true';
    playingCount += 1;
    const attempt = video.play();
    if (attempt && attempt.catch) {
      attempt.catch(() => {
        release(video);
      });
    }
  };

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;
        if (entry.isIntersecting) {
          tryPlay(video);
        } else if (!video.paused) {
          video.pause();
        }
      });
    },
    { threshold: 0.3 }
  );

  const register = (video) => {
    if (video.dataset.sqmManaged) return;
    video.dataset.sqmManaged = 'true';
    // The count is driven by the synchronous RESERVATION (see tryPlay), not by
    // the async 'play' event. We only listen for pause to free the slot when a
    // video stops (IO pause, end-of-media edge, or the user/UA pausing it).
    video.addEventListener('pause', () => {
      release(video);
    });
    observer.observe(video);
  };

  window.__sqmVideo = { register };
  document.querySelectorAll('video[data-sqm-video]').forEach(register);

  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('video[data-sqm-video]').forEach(register);
  });

  reduceMotion.addEventListener('change', (event) => {
    if (event.matches) {
      document.querySelectorAll('video[data-sqm-video]').forEach((video) => {
        if (!video.paused) video.pause();
      });
    }
  });
})();
